import { Incident } from '../Types/Incident';
import { IncidentStatus } from '../Types/enums';
import { getIncidentScopeCounts, getNeedsAttentionCount } from '../features/incidents/accountabilityScope';
import type { IncidentAssignment } from '../Types/IncidentAssignment';

/**
 * Returns true when the incident is in a closed/completed/inactive state.
 * Handles string status values from the API.
 */
export const isIncidentClosed = (incident?: Incident | { status?: number | string } | null): boolean => {
  if (!incident || incident.status == null) return false;
  const s = incident.status;
  if (typeof s === 'string') {
    const lower = s.toLowerCase().trim();
    return lower === IncidentStatus.Closed || ['completed', 'complete', 'inactive'].includes(lower);
  }
  // Legacy numeric fallback
  if (typeof s === 'number') return s === 1;
  return false;
};

/**
 * Returns true when the current user is allowed to update roster status
 * on a closed incident.
 *
 * Rules:
 * - Admin → always allowed
 * - IncidentCommander → allowed if they have an active assignment for the incident
 * - OfficeDirector → allowed if they have an active assignment for the incident
 */
export const canEditClosedRoster = (opts: {
  hasAnyRole: (roles: string[]) => boolean;
  currentUserId: string | undefined;
  assignments: IncidentAssignment[];
}): boolean => {
  const { hasAnyRole, currentUserId, assignments } = opts;

  // Admin always allowed
  if (hasAnyRole(['Admin'])) return true;

  // IncidentCommander or OfficeDirector with an active assignment
  if (!currentUserId) return false;
  if (!hasAnyRole(['IncidentCommander', 'OfficeDirector'])) return false;

  return assignments.some(
    (a) =>
      a.isActive &&
      (a.appUserId === currentUserId || a.externalUserId === currentUserId) &&
      (a.assignmentRoleName === 'IncidentCommander' || a.assignmentRoleName === 'OfficeDirector'),
  );
};

// ── Operational state for active incidents ──

export type IncidentOperationalState =
  | 'closed'
  | 'emptyRoster'
  | 'needsAttention'
  | 'complete'
  | 'notStarted'
  | 'inProgress';

export const getIncidentOperationalState = (incident: Incident): IncidentOperationalState => {
  if (isIncidentClosed(incident)) return 'closed';

  const scope = getIncidentScopeCounts(incident);
  const ac = scope.accountabilityCounts;
  const needsAttention = getNeedsAttentionCount(scope);

  if (ac.total === 0) return 'emptyRoster';
  if (needsAttention > 0) return 'needsAttention';
  if (ac.notChecked === 0 && needsAttention === 0) return 'complete';
  if (ac.accounted === 0 && needsAttention === 0 && ac.notChecked === ac.total) return 'notStarted';
  return 'inProgress';
};

/** Sort priority for operational states (lower = more urgent). */
const STATE_SORT_ORDER: Record<IncidentOperationalState, number> = {
  needsAttention: 0,
  inProgress: 1,
  notStarted: 2,
  emptyRoster: 3,
  complete: 4,
  closed: 5,
};

/** Sort active incidents by operational urgency then startedAtUtc descending. */
export const sortByOperationalUrgency = (incidents: Incident[]): Incident[] =>
  [...incidents].sort((a, b) => {
    const stateA = getIncidentOperationalState(a);
    const stateB = getIncidentOperationalState(b);
    const orderDiff = STATE_SORT_ORDER[stateA] - STATE_SORT_ORDER[stateB];
    if (orderDiff !== 0) return orderDiff;
    // Within needsAttention, sort by notAccounted desc
    if (stateA === 'needsAttention') {
      const naDiff = (b.currentCounts?.notAccounted ?? 0) - (a.currentCounts?.notAccounted ?? 0);
      if (naDiff !== 0) return naDiff;
    }
    // Then by startedAtUtc descending (newest first)
    return new Date(b.startedAtUtc).getTime() - new Date(a.startedAtUtc).getTime();
  });
